import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '@utils/api';
import { useI18n } from '@/i18n';
import logoWordmark from '../assets/logo-glk-wordmark.png';

// Public page reached from the invite email: /set-password?token=…
// Validates the token, lets the staff member choose a password, activates the
// account, then routes to login.
export default function SetPassword() {
  const { t }      = useI18n();
  const [params]   = useSearchParams();
  const navigate   = useNavigate();
  const token      = params.get('token') || '';

  const [phase,    setPhase]    = useState('checking'); // checking | ready | invalid | done
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!token) { setPhase('invalid'); return; }
      try {
        const { data } = await api.get('/access/validate', { params: { token } });
        if (!alive) return;
        if (data.valid) { setEmail(data.email || ''); setPhase('ready'); }
        else            { setPhase('invalid'); }
      } catch {
        if (alive) setPhase('invalid');
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 10)          { setError(t('setpw.errShort')); return; }
    if (!/[A-Za-z]/.test(password) || !/[0-9]/.test(password)) { setError(t('setpw.errComplexity')); return; }
    if (password !== confirm)          { setError(t('setpw.errMatch')); return; }
    setLoading(true);
    try {
      await api.post('/access/set-password', { token, password });
      setPhase('done');
      setTimeout(() => navigate('/login', { replace: true }), 2200);
    } catch (err) {
      setError(err.response?.data?.message || t('setpw.errGeneric'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            'radial-gradient(90% 60% at 50% -10%, rgba(45,125,70,0.18), transparent 60%), ' +
            'radial-gradient(70% 50% at 90% 110%, rgba(26,94,158,0.14), transparent 60%), #F7F8FA',
        }}
      />
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-sm"
      >
        <div className="mb-8 text-center">
          <img src={logoWordmark} alt="GroundLink III Handling" className="h-16 w-auto mx-auto" />
          <p className="mt-3 text-xs font-mono text-gray-400 uppercase tracking-[0.2em]">{t('common.portalName')}</p>
        </div>

        <div className="card p-8 backdrop-blur-sm bg-white/90">
          {phase === 'checking' && (
            <div className="flex flex-col items-center gap-3 py-6">
              <div className="w-8 h-8 rounded-lg bg-glk-green animate-pulse" />
              <span className="text-xs font-mono text-gray-400 uppercase tracking-widest">{t('setpw.checking')}</span>
            </div>
          )}

          {phase === 'invalid' && (
            <div className="text-center py-4">
              <h1 className="text-lg font-semibold text-glk-dark mb-2">{t('setpw.invalidTitle')}</h1>
              <p className="text-sm text-gray-500 mb-6">{t('setpw.invalidLead')}</p>
              <button onClick={() => navigate('/login')} className="btn-primary w-full">{t('setpw.goLogin')}</button>
            </div>
          )}

          {phase === 'done' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-6">
              <div className="mx-auto mb-4 grid place-items-center w-12 h-12 rounded-full bg-green-100 text-green-700">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
              </div>
              <h1 className="text-lg font-semibold text-glk-dark mb-1">{t('setpw.doneTitle')}</h1>
              <p className="text-sm text-gray-500">{t('setpw.doneLead')}</p>
            </motion.div>
          )}

          {phase === 'ready' && (
            <>
              <h1 className="text-lg font-semibold text-glk-dark mb-1">{t('setpw.title')}</h1>
              <p className="text-sm text-gray-500 mb-6">
                {email ? <>{t('setpw.account')} <span className="font-medium text-glk-dark">{email}</span></> : t('setpw.chooseLead')}
              </p>

              {error && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mb-4 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700"
                >
                  {error}
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="password" className="label">{t('setpw.password')}</label>
                  <input id="password" type="password" autoComplete="new-password" required className="input"
                         value={password} onChange={(e) => setPassword(e.target.value)} />
                  <p className="mt-1 text-[11px] text-gray-400">{t('setpw.hint')}</p>
                </div>
                <div>
                  <label htmlFor="confirm" className="label">{t('setpw.confirm')}</label>
                  <input id="confirm" type="password" autoComplete="new-password" required className="input"
                         value={confirm} onChange={(e) => setConfirm(e.target.value)} />
                </div>
                <motion.button type="submit" disabled={loading} whileTap={{ scale: 0.98 }} className="btn-primary w-full mt-2">
                  {loading ? t('setpw.activating') : t('setpw.activate')}
                </motion.button>
              </form>
            </>
          )}
        </div>

        <p className="mt-6 text-center text-[11px] text-gray-400">
          {t('common.footer')}
        </p>
      </motion.div>
    </div>
  );
}
